import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Check, Zap, Infinity, ArrowLeft, Loader2, GraduationCap, FlaskConical, Building2, CreditCard } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { logger } from '@/lib/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PLANS = [
  {
    id: 'student',
    name: 'Student',
    price: 9,
    analyses: 50,
    icon: GraduationCap,
    color: 'from-green-500 to-emerald-600',
    features: [
      '50 analyses per month',
      'All analysis types',
      'PDF report generation',
      'Multi-language support',
      'Email support'
    ]
  },
  {
    id: 'research',
    name: 'Research',
    price: 29,
    analyses: 200,
    icon: FlaskConical,
    popular: true,
    color: 'from-blue-500 to-cyan-600',
    features: [
      '200 analyses per month',
      'All analysis types',
      'PDF report generation',
      'Multi-language support',
      'Priority email support',
      'Advanced analytics'
    ]
  },
  {
    id: 'lab',
    name: 'Lab',
    price: 99,
    analyses: -1,
    icon: Building2,
    color: 'from-purple-500 to-indigo-600',
    features: [
      'Unlimited analyses',
      'All analysis types',
      'PDF report generation',
      'Multi-language support',
      '24/7 priority support',
      'Advanced analytics',
      'Team collaboration'
    ]
  }
];

const SubscriptionPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processingPlan, setProcessingPlan] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('stripe');

  useEffect(() => {
    fetchSubscription();
    
    // Handle PayPal return
    const paypalReturn = searchParams.get('paypal');
    const paymentId = searchParams.get('paymentId');
    const payerId = searchParams.get('PayerID');
    const plan = searchParams.get('plan');
    
    if (paypalReturn && paymentId && payerId) {
      executePayPalPayment(paymentId, payerId);
    }
  }, [searchParams]);

  const fetchSubscription = async () => {
    try {
      const response = await axios.get(`${API}/subscription`, {
        withCredentials: true,
      });
      // Backend nests the subscription document under `.subscription` and
      // returns sibling fields (can_analyze, plan_name, plans). Flatten so
      // the JSX below can keep using `subscription.plan`, `.analyses_used`,
      // `.analyses_limit`, `.status`, `.can_analyze`, `.plan_name`.
      const sub = response.data?.subscription ?? {};
      setSubscription({
        ...sub,
        can_analyze: response.data?.can_analyze ?? sub.can_analyze,
        plan_name: response.data?.plan_name ?? sub.plan_name,
      });
    } catch (error) {
      logger.error('Failed to load subscription:', error);
      toast.error('Failed to load subscription');
    } finally {
      setLoading(false);
    }
  };

  const executePayPalPayment = async (paymentId, payerId) => {
    try {
      const response = await axios.post(
        `${API}/paypal/execute?payment_id=${paymentId}&payer_id=${payerId}`,
        {},
        { withCredentials: true }
      );
      toast.success('Payment successful! Your subscription is now active.');
      fetchSubscription();
      navigate('/subscription/success');
    } catch (error) {
      logger.error('PayPal execution error:', error);
      toast.error('Payment failed. Please try again.');
    }
  };

  const handleSubscribe = async (planId) => {
    setProcessingPlan(planId);
    try {
      // Backend contract: both /checkout and /paypal/checkout expect
      // `plan` + `success_url` + `cancel_url` (not `origin_url`).
      // Response shape differs:
      //   Stripe  → response.data.checkout_url
      //   PayPal  → response.data.approval_url
      const origin = window.location.origin;
      const success_url = `${origin}/subscription?checkout=success`;
      const cancel_url  = `${origin}/subscription?checkout=cancel`;
      
      if (paymentMethod === 'paypal') {
        const response = await axios.post(
          `${API}/paypal/checkout`,
          { plan: planId, success_url, cancel_url },
          { withCredentials: true }
        );
        window.location.href = response.data.approval_url;
      } else {
        const response = await axios.post(
          `${API}/checkout`,
          { plan: planId, success_url, cancel_url },
          { withCredentials: true }
        );
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      logger.error('Checkout error:', error);
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
      setProcessingPlan(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  const currentPlan = subscription?.plan || 'free';
  const analysesUsed = subscription?.analyses_used || 0;
  const analysesLimit = subscription?.analyses_limit || 5;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 pb-12">
      <header className="bg-slate-900/80 backdrop-blur-md border-b border-slate-700 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-slate-300 hover:text-white hover:bg-slate-700"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Current Subscription */}
        {subscription && (
          <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-8 mb-12">
            <h2 className="text-2xl font-bold text-white mb-4">Current Subscription</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <div>
                <p className="text-sm text-slate-400 mb-1">Plan</p>
                <p className="text-xl font-bold text-white capitalize">
                  {subscription.plan_name}
                </p>
              </div>
              <div>
                <p className="text-sm text-slate-400 mb-1">Analyses Used</p>
                <p className="text-xl font-bold text-white">
                  {analysesUsed} / {analysesLimit === -1 ? '∞' : analysesLimit}
                </p>
                <div className="mt-2 h-2 bg-slate-700 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full transition-all"
                    style={{ width: analysesLimit === -1 ? '10%' : `${Math.min((analysesUsed / analysesLimit) * 100, 100)}%` }}
                  />
                </div>
              </div>
              <div>
                <p className="text-sm text-slate-400 mb-1">Status</p>
                <span className="inline-block px-3 py-1 rounded-full text-sm font-medium bg-green-500/20 text-green-400 border border-green-500/30">
                  {subscription.status}
                </span>
              </div>
            </div>
            {!subscription.can_analyze && currentPlan === 'free' && (
              <div className="mt-6 p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                <p className="text-amber-400 font-medium">
                  You've used all your free analyses this month. Subscribe to continue analyzing!
                </p>
              </div>
            )}
          </div>
        )}

        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">
            Choose Your <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">Plan</span>
          </h1>
          <p className="text-lg text-slate-400">Select the perfect plan for your laboratory needs</p>
        </div>

        {/* Free Plan Banner */}
        <div className="bg-slate-800/30 border border-slate-700 rounded-xl p-6 mb-8 text-center">
          <div className="flex items-center justify-center gap-3 mb-2">
            <Zap className="w-5 h-5 text-cyan-400" />
            <span className="text-white font-semibold">Free Plan</span>
          </div>
          <p className="text-slate-400">
            All users get <span className="text-cyan-400 font-bold">5 free analyses per month</span>. Upgrade for more!
          </p>
        </div>

        {/* Payment Method Selector */}
        <div className="max-w-md mx-auto mb-12">
          <p className="text-center text-slate-400 mb-4">Select payment method</p>
          <div className="flex gap-4 justify-center">
            <button
              onClick={() => setPaymentMethod('stripe')}
              className={`flex items-center gap-3 px-6 py-4 rounded-xl border transition-all ${
                paymentMethod === 'stripe'
                  ? 'bg-slate-700 border-cyan-500 text-white'
                  : 'bg-slate-800/50 border-slate-700 text-slate-400 hover:border-slate-600'
              }`}
            >
              <CreditCard className="w-5 h-5" />
              <div className="text-left">
                <div className="font-semibold">Credit Card</div>
                <div className="text-xs text-slate-500">Visa, Mastercard, Amex</div>
              </div>
            </button>
            <button
              onClick={() => setPaymentMethod('paypal')}
              className={`flex items-center gap-3 px-6 py-4 rounded-xl border transition-all ${
                paymentMethod === 'paypal'
                  ? 'bg-slate-700 border-cyan-500 text-white'
                  : 'bg-slate-800/50 border-slate-700 text-slate-400 hover:border-slate-600'
              }`}
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19a.563.563 0 0 0-.556.479l-.59 3.727a.466.466 0 0 1-.46.4h-1.563z"/>
              </svg>
              <div className="text-left">
                <div className="font-semibold">PayPal</div>
                <div className="text-xs text-slate-500">Pay with PayPal</div>
              </div>
            </button>
          </div>
        </div>

        {/* Plans Grid */}
        <div className="grid md:grid-cols-3 gap-8">
          {PLANS.map((plan) => {
            const isCurrentPlan = currentPlan === plan.id;
            const isProcessing = processingPlan === plan.id;
            const IconComponent = plan.icon;

            return (
              <Card
                key={plan.id}
                className={`relative bg-slate-800/50 border rounded-2xl p-8 ${
                  plan.popular
                    ? 'border-cyan-500/50 shadow-xl shadow-cyan-500/10'
                    : 'border-slate-700'
                } hover:border-cyan-500/30 hover:shadow-xl transition-all duration-300`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white px-4 py-1 rounded-full text-sm font-semibold shadow-lg">
                      Most Popular
                    </span>
                  </div>
                )}

                <div className="text-center mb-6">
                  <div className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br ${plan.color} mb-4 shadow-lg`}>
                    <IconComponent className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-2">{plan.name}</h3>
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="text-5xl font-bold text-white">${plan.price}</span>
                    <span className="text-slate-400">/month</span>
                  </div>
                  <p className="text-slate-400 mt-2">
                    {plan.analyses === -1 ? (
                      <span className="flex items-center justify-center gap-1 text-purple-400">
                        <Infinity className="w-5 h-5" />
                        Unlimited analyses
                      </span>
                    ) : (
                      `${plan.analyses.toLocaleString()} analyses/month`
                    )}
                  </p>
                </div>

                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-300">{feature}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  onClick={() => handleSubscribe(plan.id)}
                  disabled={isCurrentPlan || isProcessing}
                  className={`w-full py-6 text-lg font-semibold ${
                    plan.popular
                      ? 'bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white'
                      : 'bg-slate-700 hover:bg-slate-600 text-white'
                  }`}
                >
                  {isProcessing ? (
                    <span className="flex items-center justify-center gap-2">
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Processing...
                    </span>
                  ) : isCurrentPlan ? (
                    'Current Plan'
                  ) : (
                    'Subscribe Now'
                  )}
                </Button>
              </Card>
            );
          })}
        </div>

        {/* Payment Methods */}
        <div className="mt-16 text-center">
          <p className="text-slate-400 mb-4">Secure payments powered by</p>
          <div className="flex items-center justify-center gap-6">
            <div className="bg-slate-800/50 border border-slate-700 rounded-lg px-4 py-2">
              <span className="text-white font-semibold">Stripe</span>
            </div>
            <div className="bg-slate-800/50 border border-slate-700 rounded-lg px-4 py-2">
              <span className="text-[#00457C] font-semibold">PayPal</span>
            </div>
          </div>
          <p className="text-slate-500 text-sm mt-4">
            Credit/Debit Cards • Apple Pay • Google Pay • PayPal
          </p>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionPage;
